import {
  Box,
  Button,
  Flex,
  PopoverHeader,
  Select,
  Text
} from '@chakra-ui/react';
import React from 'react';

import type { DictionaryOption } from '../../interface/api';
import {
  BAR_BG,
  FIELD_LABEL_DIVIDER,
  INK,
  INK_MUTED,
  INK_SOFT
} from '../../styles/tokens';

const INTER_STACK =
  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export type CaseSpecialtyEditStatus = 'loading' | 'ready' | 'unavailable' | 'saving';

export type CaseSpecialtyEditFormProps = {
  readonly currentSpecialty: string;
  readonly currentDepartment: string;
  readonly currentDepartmentCode: string;
  readonly billingArea: string;
  readonly specialtyOptions: readonly DictionaryOption[];
  readonly departmentOptions: readonly DictionaryOption[];
  readonly status: CaseSpecialtyEditStatus;
  readonly errorMessage?: string;
  readonly canSave: boolean;
  readonly specialtySelectRef: React.RefObject<HTMLSelectElement>;
  readonly onSave: (
    specialty: string,
    department: string,
    departmentCode: string | undefined
  ) => void;
  readonly onCancel: () => void;
};

const FIELD_LABEL_PROPS = {
  fontFamily: INTER_STACK,
  fontSize: '11px',
  fontWeight: 500,
  color: INK_MUTED,
  whiteSpace: 'nowrap'
} as const;

const ACTION_BUTTON_PROPS = {
  fontFamily: INTER_STACK,
  fontSize: '11px',
  fontWeight: 600,
  height: 'auto',
  minWidth: 'auto',
  padding: '4px 10px',
  borderRadius: '5px',
  border: '1px solid',
  borderColor: FIELD_LABEL_DIVIDER,
  cursor: 'pointer'
} as const;

const WRITE_UNAVAILABLE = "Saving isn't available for this case.";
const CURRENT_DEPARTMENT = '\u0000current-department';

export const CaseSpecialtyEditForm: React.FC<CaseSpecialtyEditFormProps> = ({
  currentSpecialty,
  currentDepartment,
  currentDepartmentCode,
  billingArea,
  specialtyOptions,
  departmentOptions,
  status,
  errorMessage,
  canSave,
  specialtySelectRef,
  onSave,
  onCancel
}) => {
  const [specialtySelection, setSpecialtySelection] = React.useState(
    currentSpecialty
  );
  const [pickedDepartment, setPickedDepartment] = React.useState<
    string | undefined
  >(undefined);

  const isSaving = status === 'saving';
  const specialtyIsListed = specialtyOptions.some(
    (option) => option.value === currentSpecialty
  );
  const matchedDepartment =
    currentDepartmentCode === ''
      ? undefined
      : departmentOptions.find(
          (option) => option.pairedValue === currentDepartmentCode
        );
  const initialDepartment = matchedDepartment?.value ?? CURRENT_DEPARTMENT;
  const departmentSelection = pickedDepartment ?? initialDepartment;

  const save = () => {
    if (departmentSelection === CURRENT_DEPARTMENT) {
      onSave(
        specialtySelection,
        currentDepartment,
        currentDepartmentCode || undefined
      );
      return;
    }
    onSave(
      specialtySelection,
      departmentSelection,
      departmentOptions.find((option) => option.value === departmentSelection)
        ?.pairedValue
    );
  };
  const notice = errorMessage ?? (canSave ? undefined : WRITE_UNAVAILABLE);

  return (
    <Box data-testid="case-specialty-edit-form">
      <PopoverHeader
        border="none"
        padding="0 0 8px"
        fontFamily={INTER_STACK}
        fontSize="12.5px"
        fontWeight={700}
        color={INK}
      >
        Edit case specialty
      </PopoverHeader>
      <Box
        display="grid"
        gridTemplateColumns="auto 1fr"
        columnGap="12px"
        rowGap="6px"
        alignItems="center"
      >
        <Text as="label" {...FIELD_LABEL_PROPS}>
          Case Specialty
        </Text>
        <Select
          ref={specialtySelectRef}
          data-testid="case-specialty-edit-select"
          aria-label="Case specialty"
          size="sm"
          fontFamily={INTER_STACK}
          fontSize="12px"
          value={specialtySelection}
          isDisabled={status !== 'ready'}
          onChange={(event) => setSpecialtySelection(event.target.value)}
        >
          {!specialtyIsListed && (
            <option value={currentSpecialty}>{currentSpecialty || 'N/A'}</option>
          )}
          {specialtyOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <Text as="label" {...FIELD_LABEL_PROPS}>
          Department
        </Text>
        <Select
          data-testid="case-specialty-edit-department-select"
          aria-label="Department"
          size="sm"
          fontFamily={INTER_STACK}
          fontSize="12px"
          value={departmentSelection}
          isDisabled={status !== 'ready'}
          onChange={(event) => setPickedDepartment(event.target.value)}
        >
          {!matchedDepartment && (
            <option value={CURRENT_DEPARTMENT}>
              {currentDepartment || 'N/A'}
            </option>
          )}
          {departmentOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <Text as="span" {...FIELD_LABEL_PROPS}>
          Billing Area
        </Text>
        <Text
          as="span"
          data-testid="case-specialty-edit-billing-area"
          fontFamily={INTER_STACK}
          fontSize="12px"
          fontWeight={500}
          color={INK}
          paddingX="12px"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {billingArea}
        </Text>
      </Box>
      {notice && (
        <Text
          as="p"
          role="alert"
          data-testid="case-specialty-edit-error"
          fontFamily={INTER_STACK}
          fontSize="11px"
          fontWeight={500}
          color="red.600"
          marginTop="6px"
        >
          {notice}
        </Text>
      )}
      <Flex justifyContent="flex-end" gap="6px" marginTop="10px">
        <Button
          data-testid="case-specialty-edit-cancel"
          {...ACTION_BUTTON_PROPS}
          bg={BAR_BG}
          color={INK_SOFT}
          _hover={{ borderColor: INK_MUTED }}
          _active={{ borderColor: INK_MUTED }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-testid="case-specialty-edit-save"
          {...ACTION_BUTTON_PROPS}
          bg={INK}
          color="white"
          borderColor={INK}
          _hover={{
            bg: '#000000',
            borderColor: '#000000',
            _disabled: { bg: INK, borderColor: INK }
          }}
          _active={{ bg: '#000000', borderColor: '#000000' }}
          _disabled={{ opacity: 0.45, cursor: 'not-allowed' }}
          isLoading={isSaving}
          isDisabled={
            !canSave ||
            status !== 'ready' ||
            (specialtySelection === currentSpecialty &&
              departmentSelection === initialDepartment)
          }
          onClick={save}
        >
          Save
        </Button>
      </Flex>
    </Box>
  );
};
